import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';
import { Industry } from '../../../stores/industryStore';
import { useProfileStore } from '../../../stores/profileStore';
import { useAdminStore } from '../../../stores/adminStore';
import GenericInformationModal from '../../common/GenericInformationModal';
import { getDisplayErrorMessage } from '../../../utils/errors';

interface Props {
  industry: Industry | null;
  onClose: () => void;
}

const IndustryModalInfo: React.FC<Props> = ({ industry, onClose }) => {
  const { t } = useTranslation(['common', 'admin', 'notifications']);
  const { approveSuggestedIndustry } = useProfileStore();
  const { deleteIndustry } = useAdminStore();

  const [validateErrorMessage, setValidateErrorMessage] = useState<string | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

  const [validateSuccess, setValidateSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  if (!industry) return null;

  const handleValidate = async () => {
    const result = await approveSuggestedIndustry(industry.id);
    if (result === null) {
      setValidateSuccess(true);
    } else {
      setValidateSuccess(false);
      setValidateErrorMessage(
        getDisplayErrorMessage(
          result,
          t('industryApprovalErrorFallback', {
            ns: 'notifications',
            defaultValue: 'Could not approve industry.',
          })
        )
      );
    }
  };

  const handleDelete = async () => {
    if (!industry) return;
    try {
      await deleteIndustry(industry.id);
      setDeleteSuccess(true);
    } catch (error) {
      console.error(error);
      setDeleteErrorMessage(
        getDisplayErrorMessage(
          error,
          t('industryDeleteErrorFallback', {
            ns: 'admin',
            defaultValue: 'Could not delete industry.',
          })
        )
      );
    }
  };

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[40rem]">
        <h2 className="text-2xl font-bold text-secondary mb-8">
          {t('industry_details', { ns: 'admin', defaultValue: 'Industry Details' })}
        </h2>

        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-start">
            {/* Label */}
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">
                {t('table_fields.label', { ns: 'admin' })}
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {t(`industries.${industry.slug}`, { ns: 'common', defaultValue: industry.label })}
              </p>
            </div>

            {/* Created At */}
            {industry.created_at && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.created_at', { ns: 'admin', defaultValue: 'Created At' })}
                </p>
                <p className="text-base text-gray-900">
                  {new Date(industry.created_at).toLocaleDateString()}
                </p>
              </div>
            )}

            {/* Status */}
            {industry.validated !== undefined && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.validated', { ns: 'admin', defaultValue: 'Validated' })}
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    industry.validated
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {industry.validated
                    ? t('form_fields.yes', { ns: 'common', defaultValue: 'Oui' })
                    : t('form_fields.no', { ns: 'common', defaultValue: 'Non' })}
                </span>
              </div>
            )}

            {/* Deleted */}
            {industry.deleted !== undefined && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.deleted', { ns: 'admin', defaultValue: 'Deleted' })}
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    industry.deleted ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}
                >
                  {industry.deleted
                    ? t('form_fields.yes', { ns: 'common', defaultValue: 'Oui' })
                    : t('form_fields.no', { ns: 'common', defaultValue: 'Non' })}
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          {industry.industry_description && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-500 mb-2">
                {t('table_fields.description', { ns: 'admin' })}
              </p>
              <p className="text-base text-gray-900 leading-relaxed">
                {industry.industry_description}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-end items-center gap-4">
          <Button
            variant="simple-text"
            onClick={onClose}
            className="font-bold rounded-full px-8 py-2 w-full sm:w-auto order-2 sm:order-1"
            type="button"
          >
            {t('common:form_actions.return', { ns: 'common', defaultValue: 'Return' })}
          </Button>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto order-1 sm:order-2">
            {!industry.validated && (
              <Button
                variant="primary"
                onClick={handleValidate}
                className="font-bold rounded-full px-8 py-2 w-full sm:w-auto"
                type="button"
              >
                {t('button.approveAndMakeVisibleForAll', { ns: 'notifications' })}
              </Button>
            )}

            <Button
              onClick={handleDelete}
              className="bg-high-contrast-error text-white font-bold rounded-full px-8 py-2 w-full sm:w-auto"
              type="button"
            >
              {t('common:form_actions.remove_industry', {
                ns: 'common',
                defaultValue: 'Supprimer le domaine',
              })}
            </Button>
          </div>
        </div>
      </FullBlockingModal>

      {/* Confirmation Modal */}
      {deleteSuccess ? (
        <GenericInformationModal
          title={t('industryDeleted', { ns: 'admin' })}
          message=""
          onClose={() => {
            setDeleteSuccess(false);
            onClose();
          }}
          closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
        />
      ) : (
        deleteErrorMessage && (
          <GenericInformationModal
            title={t('industryDeleteError', { ns: 'admin' })}
            message={t('industryDeleteError', { ns: 'admin' })}
            onClose={() => {
              setDeleteErrorMessage(null);
            }}
            errorDetails={deleteErrorMessage}
            closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
          />
        )
      )}

      {validateSuccess ? (
        <GenericInformationModal
          title={t('industryApproved', { ns: 'notifications' })}
          message=""
          onClose={() => {
            setValidateSuccess(false);
            onClose();
          }}
          closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
        />
      ) : (
        validateErrorMessage && (
          <GenericInformationModal
            title={t('industryApprovalError', { ns: 'notifications' })}
            message=""
            onClose={() => {
              setValidateErrorMessage(null);
              onClose();
            }}
            errorDetails={validateErrorMessage}
            closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
          />
        )
      )}
    </>
  );
};

export default IndustryModalInfo;
